import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type Locale = "zh" | "en";

const STORAGE_KEY = "accsci-locale";

const en: Record<string, string> = {
  加速科学: "Acceleration Science",
  "加速科学 AccSci 首页": "AccSci home",
  关于: "About",
  关于我们: "About",
  关于加速科学: "About AccSci",
  产品: "Products",
  服务: "Services",
  能力: "Services",
  客户案例: "Work",
  项目咨询: "Start a project",
  提交想法: "Share your idea",
  主导航: "Primary",
  打开菜单: "Open menu",
  关闭菜单: "Close menu",
  导航: "Navigation",
  联系: "Contact",
  跳到主要内容: "Skip to main content",
  返回首页: "Back home",
  "这里没有找到系统。": "We couldn't find that page.",
  "页面可能已移动，或者这个路径尚未接入。":
    "It may have moved, or this route has not been connected yet.",
  "从 AI 应用到智能硬件，构建真正进入现实世界的产品。":
    "AI-native product engineering, from intelligent software to connected products.",
  "应用 · 云边端 · 智能硬件": "Idea · Prototype · Product · Scale",
  告诉我们你的想法: "Share your idea",
  查看真实案例: "See real work",
  查看项目案例: "See real work",
  探索技术能力: "Explore services",
  查看完整技术能力: "Explore all services",
  和我们聊聊: "Talk to us",
  "让智能从想法\n进入产品与现场。":
    "Turn an idea into a product that works in the real world.",

  "LLM / VLM / 多模态应用": "LLM / VLM / Multimodal Applications",
  "让语言、图像、语音与结构化数据进入同一业务上下文。":
    "Bring language, images, speech, and structured data into one business context.",
  "面向复杂任务的规划、协同、执行与状态管理。":
    "Plan, coordinate, execute, and manage state across complex tasks.",
  "构建可靠的工具注册、权限、记忆、追踪与恢复机制。":
    "Build reliable tool registries, permissions, memory, tracing, and recovery.",
  "RAG / 企业知识库": "RAG / Enterprise Knowledge",
  "把分散知识转化为可检索、可引用、可治理的模型上下文。":
    "Turn fragmented knowledge into retrievable, citable, governed model context.",
  "将判断、生成与执行嵌入可观测的业务自动化流程。":
    "Embed reasoning, generation, and execution in observable business workflows.",
  "系统设计指令、上下文窗口与长期记忆。":
    "Design system instructions, context windows, and long-term memory.",
  "Model API 与多模型路由": "Model APIs and Multi-model Routing",
  "按质量、成本、延迟与可用性动态选择模型。":
    "Route models dynamically by quality, cost, latency, and availability.",
  开源模型私有化部署: "Private Open-source Model Deployment",
  "满足数据边界、模型主权与专有环境要求。":
    "Meet data-boundary, model-sovereignty, and private-environment requirements.",
  大模型推理服务: "Large-model Inference Services",
  "围绕吞吐、延迟、弹性与可观测性构建推理基础设施。":
    "Build inference infrastructure for throughput, latency, elasticity, and observability.",
  "LoRA / SFT 模型微调": "LoRA / SFT Model Fine-tuning",
  "用高质量领域数据让模型获得稳定的专用能力。":
    "Use high-quality domain data to create stable, specialized model behavior.",
  "模型评测与 Benchmark": "Model Evaluation and Benchmarking",
  "以离线评测、在线反馈和红队测试度量真实效果。":
    "Measure real performance with offline evaluation, live feedback, and red-team testing.",
  "用标准协议连接数据、软件服务与执行环境。":
    "Connect data, software services, and execution environments through standard protocols.",
  "AI Coding 与开发者工具": "AI Coding and Developer Tools",
  "让 AI 进入研发流程，从代码理解到交付闭环。":
    "Bring AI into engineering workflows, from code understanding to delivery.",
  "语音 / 视觉 / 多模态": "Speech / Vision / Multimodal",
  "理解真实世界的声音、画面、文档与空间信息。":
    "Understand sound, images, documents, and spatial information from the real world.",
  "Edge AI / 端侧 AI": "Edge AI / On-device AI",
  "在资源受限环境中完成低延迟、隐私友好的智能推理。":
    "Deliver low-latency, privacy-friendly intelligence in constrained environments.",
  "IoT / 设备连接与控制": "IoT / Device Connectivity and Control",
  "连接传感器、执行器、控制器与云端业务系统。":
    "Connect sensors, actuators, controllers, and cloud business systems.",
  硬件方案与器件选型: "Hardware Architecture and Component Selection",
  "围绕性能、功耗、成本与供应链完成主控、传感器及外围方案设计。":
    "Select processors, sensors, and peripherals around performance, power, cost, and supply constraints.",
  "原理图与 PCB 设计": "Schematic and PCB Design",
  "覆盖原理图、Layout、高速信号、电源完整性与可制造性设计。":
    "Cover schematics, layout, high-speed signals, power integrity, and design for manufacturing.",
  嵌入式固件与驱动: "Embedded Firmware and Drivers",
  "开发 BSP、设备驱动、通信协议、实时控制与安全升级能力。":
    "Develop BSPs, drivers, communication protocols, real-time control, and secure updates.",
  工业设计与结构设计: "Industrial and Mechanical Design",
  "统筹人机交互、散热、防护、装配与产品外观的一体化设计。":
    "Unify interaction, thermal management, protection, assembly, and product form.",
  样机打样与系统联调: "Prototype Build and System Integration",
  "完成 EVT 样机、板级调试、软硬件联调与可靠性问题闭环。":
    "Build EVT prototypes, debug boards, integrate software and hardware, and close reliability issues.",
  测试认证与量产支持: "Certification and Production Support",
  "覆盖 DFM、测试工装、认证整改、供应链协同与量产导入。":
    "Support DFM, test fixtures, certification, supply-chain coordination, and production ramp.",

  拍乐租智能相机租赁柜: "Pailezu Smart Camera Rental Cabinet",
  常州镜享科技有限公司: "Changzhou Jingxiang Technology",
  "智能硬件 · 共享租赁": "Smart Hardware · Self-service Rental",
  "IoT 智能硬件与租赁系统": "IoT Hardware and Rental Platform",
  "主板对接 · 后端 · 微信小程序":
    "Board Integration · Backend · WeChat Mini Program",
  "客户项目 · 已交付": "Client Project · Delivered",
  "连接微信小程序、租赁业务后台与 IoT 柜体，实现相机从扫码租赁、开柜取机到归还结算的完整闭环。":
    "Connected a WeChat mini program, rental backend, and IoT cabinet into a complete scan-to-rent, pickup, return, and settlement flow.",
  微信小程序: "WeChat Mini Program",
  "IoT 智能柜": "IoT Smart Cabinet",
  设备控制: "Device Control",
  租赁系统: "Rental Platform",
  "高价值设备租赁，需要线上与线下始终一致。":
    "High-value equipment rental demands perfect alignment between digital orders and physical access.",
  "相机租赁同时涉及实名认证、支付押金、租赁计时、柜门控制和设备归还。任何一个环节状态不同步，都会影响用户体验和设备安全。":
    "Camera rental combines identity verification, deposits, billing, door control, and returns. Any state mismatch affects both customer experience and asset security.",
  "让每一笔线上订单，准确驱动物理柜门。":
    "Make every digital order control the correct physical door.",
  "AccSci 对接柜体硬件主板与后端设备服务，开发租赁业务后台和微信小程序，将认证、支付、押金、计费、开柜、归还与异常处理纳入同一状态闭环。":
    "AccSci integrated the cabinet controller with a device backend, rental operations console, and WeChat mini program, bringing identity, payment, deposits, billing, access, returns, and exceptions into one state machine.",
  "扫码 → 租赁 → 开柜 → 归还": "Scan → Rent → Unlock → Return",
  扫码认证: "Scan and Verify",
  支付与押金: "Payment and Deposit",
  "IoT 开柜": "IoT Unlock",
  租赁计时: "Rental Billing",
  设备归还: "Device Return",
  无人值守租赁跨越线上交易与线下设备:
    "Unattended rental connects online transactions with physical equipment",
  "微信小程序 + 业务后端 + IoT 主板":
    "WeChat mini program + operations backend + IoT controller",
  完成从扫码取机到归还结算的业务闭环:
    "Closed the loop from scan and pickup to return and settlement",
  让线上订单驱动设备: "Let digital orders drive physical devices",
  "项目完成了微信小程序、租赁后端与 IoT 柜体主板的系统连接，使用户可以自助完成认证、支付、取机、计费和归还。":
    "The finished system connects the mini program, rental backend, and cabinet controller so customers can verify, pay, collect, use, and return equipment without staff assistance.",
  拍乐租智能相机租赁柜工业设计方案示意:
    "Industrial design concept for the Pailezu smart camera rental cabinet",
  产品方案示意: "Product concept",

  "VibeHub AI 创作与应用平台": "VibeHub AI Creation and Application Platform",
  "AI 平台 · 创作者生态": "AI Platform · Creator Ecosystem",
  "AI 原生内容与应用平台": "AI-native Content and Application Platform",
  "产品设计 · 全栈开发 · 平台运营":
    "Product Design · Full-stack Engineering · Operations",
  "自研产品 · 在线运行": "AccSci Product · Live",
  "为 AI 创作者提供作品发布、浏览器即时运行、探索推荐、排行榜与开发者中心的一体化平台。":
    "An integrated platform where AI creators publish work, run it instantly in the browser, gain discovery, and learn from real users.",
  "AI 作品很多，真正的使用入口很少。":
    "AI projects are abundant; real places to use them are not.",
  "游戏、互动故事和实用工具分散在不同链接和发布渠道中，创作者难以获得持续曝光，也缺少来自真实试玩的反馈。":
    "Games, interactive stories, and tools were scattered across links and publishing channels, limiting discovery and real user feedback.",
  "把作品发布、即时运行与真实反馈放在同一平台。":
    "Put publishing, instant execution, and real feedback in one product.",
  "AccSci 构建作品上传、内容广场、浏览器运行环境、探索机制、排行榜和开发者中心，让创作者可以持续发布迭代，用户可以点开即用。":
    "AccSci built upload, discovery, browser runtime, rankings, and a developer center so creators can iterate continuously and users can launch projects in one click.",
  "发布 → 运行 → 探索 → 反馈": "Publish → Run → Discover → Learn",
  作品发布: "Publish",
  内容入库: "Catalog",
  浏览器运行: "Browser Runtime",
  探索推荐: "Discovery",
  反馈排行: "Feedback and Ranking",
  "AI 作品缺少统一发布、运行与反馈渠道":
    "AI projects lacked a unified path to publish, run, and gather feedback",
  "创作者工具 + 浏览器运行时 + 探索排行":
    "Creator tools + browser runtime + discovery and ranking",
  作品从上传进入真实试玩和持续迭代:
    "Projects move from upload to real use and continuous iteration",
  "从作品发布，到真实用户反馈": "From publishing to real user feedback",
  "平台已形成包含作品广场、即时试玩、排行榜和开发者中心的完整产品结构，并持续承载创作者发布的新作品。":
    "The platform now combines a project gallery, instant play, rankings, and a developer center, and continues to host newly published work.",
  "VibeHub AI 创作与应用平台上线页面": "Live VibeHub AI platform interface",
  已上线项目界面: "Live product interface",

  晶博工业印刷数字门户: "Jingbo Industrial Printing Digital Portal",
  苏州晶博印刷有限公司: "Suzhou Jingbo Printing",
  "工业企业 · 数字门户": "Industrial Company · Digital Portal",
  "工业 B2B 企业官网": "Industrial B2B Website",
  "内容架构 · UI 设计 · 前端开发":
    "Content Architecture · UI Design · Frontend Engineering",
  "客户项目 · 已上线": "Client Project · Live",
  "围绕工业印刷耗材、色彩管理与技术服务，建立清晰的产品信息和询价转化入口。":
    "A clear product-information and inquiry journey for industrial printing supplies, color management, and technical services.",
  "专业能力很深，但客户在线上难以快速看懂。":
    "Deep expertise was difficult for customers to understand online.",
  "油墨、橡皮布、清洗布和免冲洗版等产品信息分散，色彩管理与技术服务的价值也依赖销售人员反复解释。":
    "Product information was fragmented, while the value of color management and technical services depended on repeated sales explanations.",
  "让产品与技术内容形成清晰的客户路径。":
    "Turn products and technical knowledge into a clear customer journey.",
  "AccSci 完成网站内容架构、视觉设计和前端开发，将核心产品、色彩管理、企业能力与询价入口组织为清晰的 B2B 数字门户。":
    "AccSci delivered the content architecture, visual design, and frontend, organizing products, color expertise, company capabilities, and inquiry into a clear B2B portal.",
  "理解 → 选型 → 服务 → 询价": "Understand → Select → Support → Inquire",
  需求梳理: "Discovery",
  产品分类: "Product Structure",
  内容设计: "Content Design",
  前端开发: "Frontend Build",
  询价转化: "Inquiry Conversion",
  产品与技术服务依赖线下反复说明:
    "Products and services depended on repeated offline explanations",
  "产品信息架构 + 品牌视觉 + 询价入口":
    "Product architecture + brand experience + inquiry path",
  让工业能力在线清晰呈现并支持转化:
    "Made industrial expertise clear online and ready to convert",
  "让工业能力，在线清晰呈现": "Make industrial expertise clear online",
  "新网站把产品分类、色彩管理能力和技术服务放进统一的客户理解路径，并提供面向业务咨询的清晰入口。":
    "The new site unifies product categories, color-management expertise, and technical services into one customer journey with a clear inquiry path.",
  晶博工业印刷数字门户上线页面: "Live Jingbo industrial portal interface",

  亮典设计制作业务官网: "Liangdian Creative Services Website",
  亮典广告设计: "Liangdian Advertising Design",
  "创意服务 · 数字体验": "Creative Services · Digital Experience",
  服务型企业数字官网: "Service Business Website",
  "业务梳理 · UI 设计 · 前端开发":
    "Business Discovery · UI Design · Frontend Engineering",
  "把平面设计、印刷包装、广告标识与视频制作组织成可理解、可验证、可直接咨询的线上业务体验。":
    "Organized design, print, signage, and video services into a clear, credible online experience built for inquiries.",
  "服务品类很多，但客户看不到从创意到交付的整体能力。":
    "A broad service range hid the team's end-to-end delivery capability.",
  "设计、制作、印刷和现场安装分布在不同业务描述中，真实案例和生产能力缺少统一展示，客户难以快速判断匹配度。":
    "Design, production, printing, and installation were described separately, making it hard for customers to assess fit and delivery capability.",
  "从真实业务资料出发，把服务和交付过程讲清楚。":
    "Start from real business materials and make the service and delivery path clear.",
  "AccSci 梳理服务结构、精选制作案例和生产流程，完成网站视觉与前端开发，让客户能够理解能力、查看作品并快速发起咨询。":
    "AccSci structured the services, selected representative work and production processes, and built a site that moves customers from understanding to inquiry.",
  "需求 → 设计 → 制作 → 交付": "Discover → Design → Produce → Deliver",
  业务梳理: "Business Discovery",
  内容策划: "Content Strategy",
  案例组织: "Case Curation",
  咨询转化: "Inquiry Conversion",
  多类设计制作服务缺少统一线上表达:
    "Multiple creative services lacked a unified digital presentation",
  "业务梳理 + 案例画廊 + 转化路径":
    "Business discovery + case gallery + conversion path",
  从服务理解自然进入项目咨询:
    "Customers move naturally from understanding services to starting a project",
  "让创意服务，回到真实交付": "Bring creative services back to real delivery",
  "网站以实际服务和制作案例为核心，建立从了解能力、查看作品到直接咨询的完整访问路径。":
    "The site centers on real services and production work, creating a complete path from understanding capabilities to viewing work and making contact.",
  亮典设计制作业务官网上线页面: "Live Liangdian creative services website",

  "产品概念 · 示例": "Product Concept · Preview",
  "让 Agent 系统可编排、可观测、可治理。":
    "Make agent systems orchestrated, observable, and governable.",
  "统一管理模型、工具、上下文与执行轨迹的 Agent 运行底座。":
    "A runtime foundation for models, tools, context, and execution traces.",
  多模型路由: "Multi-model Routing",
  工具权限与沙箱: "Tool Permissions and Sandboxing",
  全链路追踪: "End-to-end Tracing",
  "AccSci Orchestrator 多模块智能编排核心概念图":
    "AccSci Orchestrator modular agent runtime concept",
  "面向现场智能的边缘计算硬件。":
    "Edge hardware for intelligence in the field.",
  "集成算力、工业接口与安全连接能力的可定制 AI 边缘网关方案。":
    "A customizable AI edge gateway combining compute, industrial interfaces, and secure connectivity.",
  "多规格 AI 算力": "Multiple AI Compute Options",
  工业协议与接口: "Industrial Protocols and Interfaces",
  "整机与 PCB 定制": "Enclosure and PCB Customization",
  "AccSci Edge Hub 工业边缘计算网关概念图":
    "AccSci Edge Hub industrial edge gateway concept",
  "从原型验证到智能硬件产品。":
    "From proof of concept to a smart hardware product.",
  "包含参考硬件、嵌入式固件、Edge Runtime 与云端设备服务的产品化底座。":
    "A product foundation spanning reference hardware, embedded firmware, edge runtime, and cloud device services.",
  参考设计与固件: "Reference Design and Firmware",
  端侧模型运行: "On-device Model Runtime",
  量产与设备管理: "Production and Device Management",
  "AccSci Device Kit 智能设备开发套件爆炸视图概念图":
    "Exploded-view concept of the AccSci Device Kit",

  系统定义: "Define",
  "同时明确业务目标、智能能力、设备形态、成本与量产约束。":
    "Align product goals, intelligent behavior, form factor, cost, and scale constraints.",
  联合原型: "Prototype",
  "用 AI 原型、开发板与结构样件验证关键体验和工程边界。":
    "Validate the core experience and engineering boundaries with software demos, development boards, and physical mockups.",
  软硬件开发: "Product Engineering",
  "并行推进应用、云服务、端侧模型、PCB、固件与结构设计。":
    "Engineer software, cloud services, edge intelligence, electronics, firmware, and mechanics as one product.",
  验证与产品化: "Pilot and Scale",
  "完成系统联调、测试认证、样机迭代与量产导入支持。":
    "Integrate the system, certify it, iterate prototypes, and support launch or production ramp.",

  车胜凯: "Shengkai Che",
  蔡羿成: "Yicheng Cai",
  苗锰: "Meng Miao",
  邢建平: "Jianping Xing",
  杨旭明: "Xuming Yang",
  王子凌: "Ziling Wang",
  北京交通大学硕士: "M.S., Beijing Jiaotong University",
  复旦大学计算机专业硕士: "M.S. in Computer Science, Fudan University",
  中山大学硕士: "M.S., Sun Yat-sen University",
  "江苏大学 · 机械电子工程": "Mechatronic Engineering, Jiangsu University",
  "拥有 knoell Group（科奈技术咨询）全栈研发及北美 AI 初创公司工作经验，专注 AI Native 应用与 Agent 系统开发，具备从产品需求、全栈系统到 LLM / Agent 工程化落地的端到端交付能力，并拥有英文技术沟通及跨国团队协作经验。":
    "Former full-stack engineer at knoell Group with experience at a North American AI startup. Focused on AI-native applications and agent systems, with end-to-end delivery experience from product definition to full-stack and LLM engineering.",
  "专注 AI 算法、嵌入式系统与 5G 通信系统优化，具备深度学习、遗传算法及多目标优化工程经验。独立完成 AI 人脸识别门禁、鸿蒙云端协同及 5G 功率放大器优化等项目，并参与 5G 智能汽车算力调度平台研发。":
    "Focused on AI algorithms, embedded systems, and 5G optimization, with engineering experience in deep learning, genetic algorithms, and multi-objective optimization. Delivered facial-recognition access control, OpenHarmony cloud-edge systems, and 5G power-amplifier optimization projects.",
  "拥有 NIO（蔚来汽车）后端研发经验，专注云原生、分布式系统与 AI 基础设施。具备 Kubernetes 高可用架构、高并发任务调度、可观测性及 RAG 系统的完整工程实践经验。":
    "Former backend engineer at NIO, focused on cloud-native systems, distributed architecture, and AI infrastructure, with hands-on experience in Kubernetes high availability, high-concurrency scheduling, observability, and RAG systems.",
  "专注智能感知、信号处理与 IoT 系统研发，曾获中国国家级技术与科研荣誉。主持省级科研创新项目，以第一作者发表 SCI JCR Q1 论文并申请国家发明专利，具备从算法研究、嵌入式开发到 IoT 系统落地的完整研发能力。":
    "Focused on intelligent sensing, signal processing, and IoT systems. Led a provincial research project, published a first-author SCI JCR Q1 paper, and filed a national invention patent, with experience spanning algorithms, embedded development, and deployed IoT systems.",
  "专注嵌入式系统、边缘计算与端云协同。具备 RK3588 5G 基站系统与 BearPi 鸿蒙端云系统开发经验，覆盖设备端、网络通信、边缘计算及云端接入。":
    "Focused on embedded systems, edge computing, and device-cloud coordination, with experience in RK3588-based 5G base-station systems and BearPi OpenHarmony device-cloud platforms.",
  "专注智能硬件的硬件与结构设计、产品外壳和快速原型开发。熟悉 SolidWorks 三维建模，可在原型阶段快速完成外壳与结构方案，并通过 3D 打印验证装配空间与结构可行性；同时具备原理图设计及单片机开发基础，能够协同推进硬件、结构与嵌入式系统的一体化落地。":
    "Focused on hardware and mechanical design, product enclosures, and rapid prototyping for intelligent devices. Proficient in SolidWorks, he can quickly develop enclosure and structural concepts, then validate fit, space, and feasibility through 3D printing. With a foundation in schematic design and microcontroller development, he works across hardware, mechanics, and embedded systems.",
  "AI 应用": "AI Applications",
  "云与 IoT": "Cloud & IoT",
  端侧智能: "Edge Intelligence",
  嵌入式系统: "Embedded Systems",
  硬件产品: "Hardware Products",
  产品化: "Productization",
  可靠: "Reliable",
  可观测: "Observable",
  可制造: "Manufacturable",
  可演进: "Evolvable",
  "失败可恢复、行为可预测、关键路径有明确的兜底机制。":
    "Recover from failure, behave predictably, and protect every critical path.",
  "从输入、检索、推理到工具执行，每一步都有完整轨迹。":
    "Trace every step from input and retrieval through reasoning and tool execution.",
  "从器件、PCB、结构到测试工装，都面向稳定生产与成本目标设计。":
    "Design components, PCB, enclosure, and test fixtures for stable production and target cost.",
  "模型、应用、固件与设备都拥有清晰的升级、维护与版本边界。":
    "Give models, applications, firmware, and devices clear upgrade and maintenance boundaries.",
};

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (value: string) => string;
  l: (zh: string, en: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "zh";
  const query = new URLSearchParams(window.location.search).get("lang");
  if (query === "en" || query === "zh") return query;
  const prerendered =
    document.getElementById("root")?.dataset.prerenderedLocale;
  if (prerendered === "en" || prerendered === "zh") return prerendered;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "zh") return stored;
  return window.navigator.language.toLowerCase().startsWith("zh") ? "zh" : "en";
}

export function translateContent<T>(value: T, locale: Locale): T {
  if (locale === "zh") return value;
  if (typeof value === "string") return (en[value] ?? value) as T;
  if (Array.isArray(value)) {
    return value.map((item) => translateContent(item, locale)) as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        translateContent(item, locale),
      ]),
    ) as T;
  }
  return value;
}

function installLocaleAwareHistory() {
  if (typeof window === "undefined") return;
  const history = window.history as History & { __accsciLocaleAware?: boolean };
  if (history.__accsciLocaleAware) return;

  const addLocale = (value?: string | URL | null) => {
    if (value == null) return value;
    const url = new URL(String(value), window.location.href);
    if (window.localStorage.getItem(STORAGE_KEY) === "en") {
      url.searchParams.set("lang", "en");
    } else {
      url.searchParams.delete("lang");
    }
    return url.href;
  };
  const nativePush = history.pushState.bind(history);
  const nativeReplace = history.replaceState.bind(history);
  history.pushState = (data, unused, url) =>
    nativePush(data, unused, addLocale(url));
  history.replaceState = (data, unused, url) =>
    nativeReplace(data, unused, addLocale(url));
  history.__accsciLocaleAware = true;
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, updateLocale] = useState<Locale>(
    initialLocale ?? getInitialLocale,
  );

  const setLocale = useCallback((nextLocale: Locale) => {
    updateLocale(nextLocale);
    window.localStorage.setItem(STORAGE_KEY, nextLocale);
    const url = new URL(window.location.href);
    if (nextLocale === "en") url.searchParams.set("lang", "en");
    else url.searchParams.delete("lang");
    window.history.replaceState(window.history.state, "", url);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, locale);
    installLocaleAwareHistory();
    document.documentElement.lang = locale === "en" ? "en" : "zh-CN";
    document.documentElement.dataset.locale = locale;
  }, [locale]);

  const t = useCallback(
    (value: string) => (locale === "en" ? (en[value] ?? value) : value),
    [locale],
  );
  const l = useCallback(
    (zh: string, english: string) => (locale === "en" ? english : zh),
    [locale],
  );

  const context = useMemo(
    () => ({ locale, setLocale, t, l }),
    [locale, setLocale, t, l],
  );

  return (
    <I18nContext.Provider value={context}>{children}</I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}

export function useLocalizedContent<T>(content: T): T {
  const { locale } = useI18n();
  return useMemo(() => translateContent(content, locale), [content, locale]);
}
